import React from 'react';
import { StanModal, StanMultiSelect } from '@/components';
import { useGoogleCalendarSettings } from '../../hooks/useGoogleCalendarSettings';
import type { IntegrationsResponse } from '@/types';

interface GoogleCalendarSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    userIntegrations: IntegrationsResponse | undefined;
    refetchIntegrations: () => void;
}

export const GoogleCalendarSettingsModal = ({
    isOpen,
    onClose,
    userIntegrations,
    refetchIntegrations,
}: GoogleCalendarSettingsModalProps) => {
    const { loading, dataLoaded, primaryCalendarName, selectedLabels, options, hasError, handleSave, handleChange } =
        useGoogleCalendarSettings({ isOpen, userIntegrations, refetchIntegrations, onClose });

    if (!isOpen) return null;

    if (!dataLoaded || options.length === 0) {
        return (
            <StanModal
                id='google-calendar-settings'
                isOpen={isOpen}
                title='Google Calendar'
                size='md'
                onClose={onClose}
            >
                <div className='p-4'>
                    <p>Loading calendars...</p>
                </div>
            </StanModal>
        );
    }

    return (
        <StanModal
            id='google-calendar-settings'
            isOpen={isOpen}
            title='Google Calendar'
            size='md'
            primaryActionLabel='Save'
            secondaryActionLabel='Cancel'
            primaryActionDisabled={loading}
            primaryActionLoading={loading}
            onPrimaryAction={handleSave}
            onSecondaryAction={onClose}
            onClose={onClose}
        >
            <div className='space-y-4'>
                <p className='text-caption-1-regular text-gray mb-4'>
                    Choose which calendar(s) you&apos;d like to connect to
                </p>

                <div>
                    <label className='text-body-2-regular mb-2 block font-medium'>Saving Appointments To:</label>
                    <div className='bg-bg-subtle rounded-lg p-4'>
                        <p className='text-body-2-regular'>{primaryCalendarName}</p>
                    </div>
                </div>

                <div>
                    <label className='text-body-2-regular mb-2 block font-medium'>Checking availability from:</label>
                    <StanMultiSelect
                        name='calendar-selection'
                        value={selectedLabels}
                        options={options}
                        onChange={handleChange}
                        placeholder='Select at least one calendar'
                        allowCreate={false}
                        disabled={loading}
                    />
                    {hasError && (
                        <p className='text-caption-1-regular text-destructive mt-2'>
                            Must select at least one calendar
                        </p>
                    )}
                </div>
            </div>
        </StanModal>
    );
};
